import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  CreditTransactionDetailModal,
  FormField,
  PortalCreditTransactionsLedger,
  SearchSelect,
  resolveCreditPeriodDateRange,
  usePortalLedgerDetailState,
  validateCreditExportPeriod,
  type CreditLedgerFilterState,
  type CreditLedgerListResponse,
  type CreditLedgerSortField,
} from '@kioskgaming/ui';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import { usePermission } from '../../../hooks/useAuth';
import { useCdnAgents, useSuperAgents } from '../../../hooks/useApi';
import { apiClient } from '../../../services/api';
import { downloadBlob } from '../../../utils';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';
import UsdWalletLedgerDetailModal from '../usdWallets/UsdWalletLedgerDetailModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card';

type OwnerType = 'all' | 'player' | 'shop' | 'agent' | 'super_agent';

const OWNER_TYPES: Array<{ value: OwnerType; label: string }> = [
  { value: 'all', label: 'All (global)' },
  { value: 'shop', label: 'Shop' },
  { value: 'agent', label: 'Agent' },
  { value: 'super_agent', label: 'Super agent' },
  { value: 'player', label: 'Player' },
];

const CreditTransactionsLedgerPage: React.FC = () => {
  const canView = usePermission('walletManagement');
  const [ownerType, setOwnerType] = useState<OwnerType>('all');
  const [ownerId, setOwnerId] = useState('');
  const [data, setData] = useState<CreditLedgerListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortFetchTick, setSortFetchTick] = useState(0);

  const tableSort = useTableSort({
    columns: getTableSortColumns('creditTransactions'),
    onChange: () => setSortFetchTick((t) => t + 1),
  });

  const hostSort = useMemo(() => {
    const sort = (data?.pagination?.sort ?? tableSort.sortBy ?? 'created') as CreditLedgerSortField;
    const sortDirRaw = data?.pagination?.sortDir ?? tableSort.sortOrder;
    const sortDir = String(sortDirRaw ?? 'desc').toLowerCase() === 'asc' ? 'asc' : 'desc';
    return { sort, sortDir: sortDir as 'asc' | 'desc' };
  }, [tableSort.sortBy, tableSort.sortOrder, data?.pagination?.sort, data?.pagination?.sortDir]);

  const { data: agentsRes } = useCdnAgents(canView && ownerType === 'agent');
  const { data: superAgentsRes } = useSuperAgents(canView && ownerType === 'super_agent');

  const ownerOptions = useMemo(() => {
    if (ownerType === 'agent') {
      return (agentsRes?.data ?? []).map((a) => ({ value: a.id, label: a.name || a.email || a.id }));
    }
    if (ownerType === 'super_agent') {
      return (superAgentsRes?.data ?? []).map((sa) => ({ value: sa.id, label: sa.name || sa.email || sa.id }));
    }
    return [];
  }, [ownerType, agentsRes, superAgentsRes]);

  const ledgerDetail = usePortalLedgerDetailState({
    creditRows: data?.items ?? [],
    fetchCreditById: async (id) => {
      const res = await apiClient.getCreditWalletLedgerEntry(id);
      return res.item;
    },
    fetchUsdById: async (id) => {
      const res = await apiClient.getUsdWalletLedgerEntry(id);
      return (res.row ?? {}) as Record<string, unknown>;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  const onFetch = useCallback(
    async ({
      filters,
      page,
      limit,
    }: {
      filters: CreditLedgerFilterState;
      page: number;
      limit: number;
    }) => {
      if (!canView) return;
      if (ownerType !== 'all' && !ownerId.trim()) {
        setData(null);
        return;
      }
      setLoading(true);
      try {
        const res = await apiClient.getCreditWalletLedger({
          ...(ownerType !== 'all' ? { ownerType, ownerId: ownerId.trim() } : {}),
          page,
          limit,
          period: filters.period,
          ...(filters.period === 'custom'
            ? { date_from: filters.dateFrom, date_to: filters.dateTo }
            : {}),
          type: filters.typeFilter,
          search: filters.search.trim() || undefined,
          sort: tableSort.sortBy ?? 'created',
          sort_dir: (tableSort.sortOrder ?? 'DESC').toLowerCase(),
          ...(tableSort.sortBy ? { sortBy: tableSort.sortBy } : {}),
          ...(tableSort.sortOrder ? { sortOrder: tableSort.sortOrder } : {}),
        });
        setData({
          items: res.items ?? res.rows ?? [],
          pagination: res.pagination ?? {
            page,
            limit,
            total: res.count ?? 0,
            totalPages: limit > 0 ? Math.ceil((res.count ?? 0) / limit) : 0,
          },
          summary: {
            totalTransactions:
              res.summary?.totalTransactions ??
              res.pagination?.total ??
              res.count ??
              0,
            totalDeposits: res.summary?.totalDeposits ?? 0,
            totalWithdrawals: res.summary?.totalWithdrawals ?? 0,
            netFlow: res.summary?.netFlow ?? 0,
          },
        });
      } catch (e: unknown) {
        setData(null);
        toast.error(e instanceof Error ? e.message : 'Could not load credit transactions');
      } finally {
        setLoading(false);
      }
    },
    [canView, ownerType, ownerId, tableSort.sortBy, tableSort.sortOrder],
  );

  const handleExport = useCallback(
    async ({ filters }: { filters: CreditLedgerFilterState }) => {
      if (ownerType !== 'all' && !ownerId.trim()) {
        toast.error('Select an owner before exporting.');
        return;
      }
      const dates = resolveCreditPeriodDateRange(filters.period, filters.dateFrom, filters.dateTo);
      const periodErr = validateCreditExportPeriod(dates.dateFrom, dates.dateTo);
      if (periodErr) {
        toast.error(periodErr);
        return;
      }
      setExporting(true);
      try {
        const { blob, filename } = await apiClient.exportCreditWalletLedger({
          ...(ownerType !== 'all' ? { ownerType, ownerId: ownerId.trim() } : {}),
          period: filters.period,
          ...(filters.period === 'custom'
            ? { date_from: dates.dateFrom, date_to: dates.dateTo }
            : {}),
          type: filters.typeFilter,
          search: filters.search.trim() || undefined,
        });
        downloadBlob(blob, filename);
        toast.success('Credit transactions exported');
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Export failed');
      } finally {
        setExporting(false);
      }
    },
    [ownerType, ownerId],
  );

  const exportDisabled = ownerType !== 'all' && !ownerId.trim();

  if (!canView) {
    return <AccessDeniedPanel message="You don't have permission to view credit transactions." />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Credit transactions</CardTitle>
        <CardDescription>
          Global ledger for all credit wallet activity. Optionally narrow to a single owner.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 p-0 sm:p-0">
        <div className="grid grid-cols-1 gap-4 px-6 pb-2 md:grid-cols-3">
          <FormField label="Scope" controlId="credit-scope-type">
            <SearchSelect
              variant="dropdown"
              options={OWNER_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              value={ownerType}
              onChange={(v) => {
                setOwnerType((v as OwnerType) || 'all');
                setOwnerId('');
              }}
              size="sm"
            />
          </FormField>
          {ownerType !== 'all' && ownerType !== 'player' ? (
            <FormField label="Owner" controlId="credit-scope-owner">
              <SearchSelect
                variant="dropdown"
                options={ownerOptions}
                value={ownerId}
                onChange={(v) => setOwnerId(typeof v === 'string' ? v : '')}
                placeholder="Select owner…"
                size="sm"
              />
            </FormField>
          ) : null}
        </div>

        {ownerType !== 'all' && !ownerId.trim() ? (
          <p className="px-6 pb-4 text-sm text-muted-foreground">Select an owner to load scoped transactions.</p>
        ) : (
          <PortalCreditTransactionsLedger
            loading={loading}
            data={data}
            onFetch={onFetch}
            onExport={handleExport}
            exporting={exporting}
            exportDisabled={exportDisabled}
            onViewDetail={(row) => ledgerDetail.openCredit(row)}
            className="border-0 shadow-none rounded-none"
            hostSort={{
              sort: hostSort.sort,
              sortDir: hostSort.sortDir,
              onSort: (field) => tableSort.toggleSort(field),
            }}
            sortFetchTick={sortFetchTick}
          />
        )}
      </CardContent>

      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Credit ledger"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
      />
      <UsdWalletLedgerDetailModal row={ledgerDetail.usdDetail} onClose={ledgerDetail.closeUsd} />
    </Card>
  );
};

export default CreditTransactionsLedgerPage;
